/**
 * Change order approval ordering + snapshot math.
 *
 * Pure module (no Firestore / React) so the client, the Cloud Function safety
 * net, and the backfill script agree on one rule:
 *
 *   prior(co) = contractValueBefore when stamped,
 *               else base + sum(deltas of COs approved BEFORE this one)
 *
 * "Approved before" orders by `approvedAt` ascending; when either date is
 * missing or the two are equal, CO `number` breaks the tie. This replaces the
 * old "base + every other approved CO" rule, which let later COs rewrite what
 * an earlier CO showed the client.
 */

import type { ChangeOrder, ProjectPLBucket } from '../types';
import { computeChangeOrderBudgetByBucket, isApprovedChangeOrder } from './contractValue';

export type SnapshotChangeOrder = Pick<
  ChangeOrder,
  | 'id'
  | 'number'
  | 'status'
  | 'deltaAmount'
  | 'approvedAt'
  | 'contractValueBefore'
  | 'contractValueAfter'
  | 'budgetByBucketBefore'
  | 'budgetByBucketAfter'
  | 'pricingMode'
  | 'lineItems'
  | 'builderNodeSets'
  | 'budgetCostByBucket'
>;

function roundMoney(n: number): number {
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** Epoch ms for approvedAt (Date, Firestore Timestamp, ISO string) or null. */
export function changeOrderApprovedTime(co: Pick<ChangeOrder, 'approvedAt'>): number | null {
  const raw = co.approvedAt as unknown;
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw.getTime();
  const maybe = raw as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (typeof maybe.toDate === 'function') {
    const d = maybe.toDate();
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const secs = typeof maybe.seconds === 'number' ? maybe.seconds : maybe._seconds;
  if (typeof secs === 'number') return secs * 1000;
  const d = new Date(raw as string);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** approvedAt ascending; missing/equal dates fall back to CO number. */
export function compareChangeOrdersByApproval(
  a: Pick<ChangeOrder, 'approvedAt' | 'number'>,
  b: Pick<ChangeOrder, 'approvedAt' | 'number'>,
): number {
  const at = changeOrderApprovedTime(a);
  const bt = changeOrderApprovedTime(b);
  if (at != null && bt != null && at !== bt) return at - bt;
  return (Number(a.number) || 0) - (Number(b.number) || 0);
}

/** Approved COs that applied before `target` (excludes `target` itself). */
export function changeOrdersApprovedBefore<T extends Pick<ChangeOrder, 'id' | 'status' | 'approvedAt' | 'number'>>(
  target: Pick<ChangeOrder, 'id' | 'status' | 'approvedAt' | 'number'>,
  all: T[],
): T[] {
  const others = all.filter(co => isApprovedChangeOrder(co) && (!target.id || co.id !== target.id));
  // A CO that is not yet approved layers on top of everything approved today.
  if (!isApprovedChangeOrder(target)) return others.sort(compareChangeOrdersByApproval);
  return others
    .filter(co => compareChangeOrdersByApproval(co, target) < 0)
    .sort(compareChangeOrdersByApproval);
}

export function sumChangeOrdersApprovedBefore(
  target: Pick<ChangeOrder, 'id' | 'status' | 'approvedAt' | 'number'>,
  all: Array<Pick<ChangeOrder, 'id' | 'status' | 'approvedAt' | 'number' | 'deltaAmount'>>,
): number {
  return changeOrdersApprovedBefore(target, all).reduce((s, co) => s + (Number(co.deltaAmount) || 0), 0);
}

/**
 * Starting point the CO ledger layers onto.
 * Signed proposal → its commercial total. Legacy deal with no CRM proposal →
 * the manually set deal value minus every approved CO (so base + all COs
 * still equals the deal value).
 */
export function contractBaseForDeal(
  signedBase: number | null | undefined,
  dealValue: number | null | undefined,
  all: Array<Pick<ChangeOrder, 'status' | 'deltaAmount'>>,
): number {
  if (typeof signedBase === 'number' && Number.isFinite(signedBase)) return roundMoney(signedBase);
  const value = Number(dealValue) || 0;
  const approved = all.reduce((s, co) => (isApprovedChangeOrder(co) ? s + (Number(co.deltaAmount) || 0) : s), 0);
  return roundMoney(value - approved);
}

/** Contract value immediately before `co` — stamped snapshot wins. */
export function priorContractValueForChangeOrder(
  co: Pick<ChangeOrder, 'id' | 'status' | 'approvedAt' | 'number' | 'contractValueBefore'>,
  base: number,
  all: Array<Pick<ChangeOrder, 'id' | 'status' | 'approvedAt' | 'number' | 'deltaAmount'>>,
): number {
  if (typeof co.contractValueBefore === 'number' && Number.isFinite(co.contractValueBefore)) {
    return co.contractValueBefore;
  }
  return roundMoney(base + sumChangeOrdersApprovedBefore(co, all));
}

export type BucketBudget = Partial<Record<ProjectPLBucket, number>>;

export function addBucketBudgets(into: BucketBudget, add: BucketBudget): BucketBudget {
  const out: BucketBudget = { ...into };
  (Object.keys(add) as ProjectPLBucket[]).forEach(bucket => {
    const amount = Number(add[bucket]) || 0;
    if (!amount) return;
    out[bucket] = roundMoney((out[bucket] || 0) + amount);
  });
  return out;
}

/** Proposal bucket budget + budgets of COs approved before `co`. */
export function budgetByBucketBeforeChangeOrder(
  co: Pick<ChangeOrder, 'id' | 'status' | 'approvedAt' | 'number' | 'budgetByBucketBefore'>,
  proposalBudget: BucketBudget,
  all: SnapshotChangeOrder[],
): BucketBudget {
  if (co.budgetByBucketBefore && Object.keys(co.budgetByBucketBefore).length > 0) {
    return co.budgetByBucketBefore;
  }
  let out: BucketBudget = { ...proposalBudget };
  for (const prior of changeOrdersApprovedBefore(co, all)) {
    out = addBucketBudgets(out, computeChangeOrderBudgetByBucket(prior));
  }
  return out;
}

export interface ChangeOrderSnapshot {
  contractValueBefore: number;
  contractValueAfter: number;
  budgetByBucketBefore: BucketBudget;
  budgetByBucketAfter: BucketBudget;
}

/** Full before/after snapshot for `co` given the deal's base and all its COs. */
export function buildChangeOrderSnapshot(
  co: SnapshotChangeOrder,
  base: number,
  proposalBudget: BucketBudget,
  all: SnapshotChangeOrder[],
): ChangeOrderSnapshot {
  const contractValueBefore = priorContractValueForChangeOrder({ ...co, contractValueBefore: undefined }, base, all);
  const budgetByBucketBefore = budgetByBucketBeforeChangeOrder({ ...co, budgetByBucketBefore: undefined }, proposalBudget, all);
  return {
    contractValueBefore,
    contractValueAfter: roundMoney(contractValueBefore + (Number(co.deltaAmount) || 0)),
    budgetByBucketBefore,
    budgetByBucketAfter: addBucketBudgets(budgetByBucketBefore, computeChangeOrderBudgetByBucket(co)),
  };
}
